import { useEffect, useState } from "react";

import { DateTime } from "luxon";

import { IPart } from "../../models/Part";
import Card from "../shared/Card/Card";
import LoadingWheel from "../shared/LoadingWheel";
import Avatar from "../user/Avatar";
import PartHistoryDrawer from "../stock/PartHistoryDrawer";
import usePartTimeline, {
  IPartTimelineEvent,
  partTimelineSourceColours,
  partTimelineSourceLabels,
  PartTimelineSource,
  useTimelineEvents,
} from "../../data/usePartTimeline";
import usePartHistoryEnabled from "../../hooks/usePartHistoryEnabled";
import { useUserLookup } from "../../hooks/useUserLookup";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";

interface ListingHistoryCardProps {
  part: IPart;
  defaultOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

// The milestones are already written as English by the API. This is only the fallback for an event
// that survived on a whitelisted field with no milestone of its own - an admin ticking an extra
// field on the Part History Fields page is what produces those.
function describeEvent(timelineEvent: IPartTimelineEvent) {
  if (timelineEvent.milestones.length > 0) return timelineEvent.milestones;

  return timelineEvent.changes.map((change) => `${change.field} changed`);
}

function HistoryRow({
  timelineEvent,
  userName,
}: {
  timelineEvent: IPartTimelineEvent;
  userName: string | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasChanges = timelineEvent.changes.length > 0;
  const lines = describeEvent(timelineEvent);

  return (
    <>
      <tr
        className={classNames(
          "border-b border-gray-200",
          hasChanges ? "cursor-pointer hover:bg-gray-50" : ""
        )}
        onClick={() => hasChanges && setExpanded(!expanded)}
      >
        <td className="px-3 py-2 align-top text-sm text-gray-700 whitespace-nowrap">
          {formatDateTime(timelineEvent.timestamp, DateTime.DATETIME_SHORT)}
        </td>
        <td className="px-3 py-2 align-top">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={classNames(
                "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
                partTimelineSourceColours[timelineEvent.source]
              )}
            >
              {partTimelineSourceLabels[timelineEvent.source]}
            </span>
            {timelineEvent.isActiveOffer && (
              <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-700">
                Active offer
              </span>
            )}
            {timelineEvent.source === PartTimelineSource.Offer && !timelineEvent.isActiveOffer && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                Old offer
              </span>
            )}
          </div>
          <ul className="mt-1 space-y-0.5">
            {lines.map((line, index) => (
              <li key={`${timelineEvent.id}-${index}`} className="text-sm font-medium text-gray-900">
                {line}
              </li>
            ))}
          </ul>
          {timelineEvent.orderNumber && (
            <p className="mt-0.5 text-xs text-gray-500">Order {timelineEvent.orderNumber}</p>
          )}
        </td>
        <td className="px-3 py-2 align-top">
          {timelineEvent.userId ? (
            <div className="flex items-center gap-1.5">
              <Avatar userId={timelineEvent.userId} classes="w-5 h-5" textClasses="!text-[10px]" />
              <span className="text-sm text-gray-700">{userName || "Unknown"}</span>
            </div>
          ) : (
            // A null user is one of the sync jobs, which run without one - worth saying so rather
            // than leaving a blank that reads like missing data
            <span className="text-xs italic text-gray-400">Sync / System</span>
          )}
          {hasChanges && (
            <span className="ml-0.5 text-xs text-gray-400">{expanded ? "▼" : "▶"}</span>
          )}
        </td>
      </tr>
      {expanded && hasChanges && (
        <tr className="border-b border-gray-200 bg-gray-50">
          <td colSpan={3} className="px-3 py-2">
            <div className="space-y-1">
              {timelineEvent.changes.map((change) => (
                <div key={`${timelineEvent.id}-${change.field}`} className="text-sm">
                  <span className="font-medium text-gray-700">{change.field}</span>
                  <div className="ml-3 break-words text-xs text-gray-500">
                    <span className="text-red-500 line-through">{change.old || <em>empty</em>}</span>
                    {" → "}
                    <span className="text-green-600">{change.new || <em>empty</em>}</span>
                  </div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * The part's intervention trail on the Active Listings expanded row - what was tried, when, and by
 * whom, so someone can tell a part that has been improved twice and discounted from one that has
 * been sat there untouched.
 *
 * Reads the same timeline endpoint as the Stock page's drawer, which the "Full history" button
 * opens for the offers list and eBay ids rather than duplicating them here. Both share the
 * ["partTimeline", partId] query key, so once this card has loaded the drawer is free.
 *
 * The fetch is gated on the card being open, the way the drawer gates on its own. The card is
 * usually shut when a row is expanded, and a timeline call per row expansion for something nobody
 * has looked at is not worth the head start.
 */
export default function ListingHistoryCard({ part, defaultOpen, onToggle }: ListingHistoryCardProps) {
  // Card treats a missing defaultOpen as open, so mirror that here rather than assuming shut -
  // otherwise an open card sits on an empty table waiting for a fetch that never starts.
  const [isOpen, setIsOpen] = useState(defaultOpen ?? true);
  const historyEnabled = usePartHistoryEnabled();
  const { partTimeline } = usePartTimeline(
    historyEnabled && isOpen ? part.id : undefined,
    "ListingHistoryCard"
  );
  const { findUser } = useUserLookup();
  const [keyEventsOnly, setKeyEventsOnly] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Card resyncs itself to defaultOpen when the prop changes, so follow it or the two disagree
  useEffect(() => {
    setIsOpen(defaultOpen ?? true);
  }, [defaultOpen]);

  const handleToggle = (open: boolean) => {
    setIsOpen(open);
    onToggle?.(open);
  };

  const timeline = partTimeline.data?.data;

  const events = useTimelineEvents(timeline?.events, keyEventsOnly);

  const hiddenCount = (timeline?.events?.length ?? 0) - events.length;

  // After the hooks so the order stays stable when the setting resolves
  if (!historyEnabled) return null;

  return (
    <>
      <PartHistoryDrawer open={drawerOpen} setOpen={setDrawerOpen} partId={part.id} />

      <Card
        title="History"
        labelId="listing-history"
        collapsible
        defaultOpen={defaultOpen}
        onToggle={handleToggle}
        bodyClassName="px-1 py-2"
        headerRight={
          <span className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
            {hiddenCount > 0 || !keyEventsOnly ? (
              <button
                type="button"
                onClick={() => setKeyEventsOnly(!keyEventsOnly)}
                className="text-xs font-medium text-primary-700 hover:text-primary-800"
              >
                {keyEventsOnly ? `Show all changes (${hiddenCount} more)` : "Key events only"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="text-xs font-medium text-primary-700 hover:text-primary-800"
            >
              Full history
            </button>
          </span>
        }
      >
        {partTimeline.isLoading ? (
          <div className="flex justify-center py-4">
            <LoadingWheel />
          </div>
        ) : !timeline ? (
          <p className="px-3 py-3 text-sm text-gray-500">Unable to load this part's history.</p>
        ) : events.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-500">
            {/* Two different empty states, and saying the wrong one sends someone hunting for a bug
                that isn't there. Changes exist but none are flagged means the API hasn't got the
                intervention milestones - everything is there behind "Show all changes". Genuinely
                nothing means the audit log, which only starts March 2026, has nothing for it. */}
            {hiddenCount > 0 ? (
              <>
                Nothing here is flagged as a key event, but this part has {hiddenCount} recorded{" "}
                {hiddenCount === 1 ? "change" : "changes"} — use <strong>Show all changes</strong> to
                see them.
              </>
            ) : (
              <>
                No recorded changes for this part. Anything done before the audit log started in
                March 2026 won't appear here.
              </>
            )}
          </p>
        ) : (
          <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-300">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700">
                    When
                  </th>
                  <th className="border-b border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700">
                    What
                  </th>
                  <th className="border-b border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700">
                    By
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {events.map((timelineEvent) => (
                  <HistoryRow
                    key={timelineEvent.id}
                    timelineEvent={timelineEvent}
                    userName={
                      timelineEvent.userId ? findUser(timelineEvent.userId)?.fullname : undefined
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
